import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { applySeo } from "../lib/seoHead";
import { fetchRedirects } from "../lib/useSeoSettings";
import { LoadingRing } from "../components/LoadingRing";

const resolve = (path, rules) => {
    for (const rule of rules) {
        if (rule.is_regex) {
            try {
                const match = new RegExp(rule.from_path).exec(path);
                if (match) return rule.to_path.replace(/\$(\d)/g, (_, i) => match[Number(i)] || "");
            } catch { /* a bad pattern should never break the page */ }
        } else {
            const from = rule.from_path.replace(/\/$/, "");
            if (path.replace(/\/$/, "") === from) return rule.to_path;
        }
    }
    return null;
};

/** Old WordPress links are sent to their new home; anything genuinely gone is kept out of the index. */
export default function NotFound() {
    const { pathname } = useLocation();
    const nav = useNavigate();
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        let alive = true;
        setChecking(true);
        fetchRedirects().then(({ rules = [] }) => {
            if (!alive) return;
            const target = resolve(pathname, rules);
            if (target && target !== pathname) nav(target, { replace: true });
            else setChecking(false);
        }).catch(() => alive && setChecking(false));
        return () => { alive = false; };
    }, [pathname, nav]);

    useEffect(() => {
        if (checking) return;
        applySeo({
            title: "Page not found — ReHealthMe",
            description: "This page has moved or no longer exists.",
            noindex: true,
        });
    }, [checking]);

    if (checking) {
        return (
            <div className="pt-40 pb-40 flex justify-center" data-testid="not-found-checking">
                <LoadingRing size={44} />
            </div>
        );
    }

    return (
        <div className="pt-40 pb-32 px-6 text-center" data-testid="not-found-page">
            <div className="eyebrow mb-4">Error 404</div>
            <h1 className="font-display text-5xl md:text-6xl uppercase tracking-tight">Page not found</h1>
            <p className="mt-5 text-zinc-400 max-w-md mx-auto">
                The page you were looking for has moved or never existed. Let's get you back to something useful.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Link to="/" className="btn-pill btn-primary" data-testid="not-found-home-link">Back to Home</Link>
                <Link to="/shop" className="btn-pill btn-secondary" data-testid="not-found-shop-link">Shop ReHealthMe</Link>
            </div>
        </div>
    );
}
